// src/pages/auth/Register.js
// Page d'inscription connectée à l'API - fusionnee avec la creation du profil Fynho Social

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { MapContainer, TileLayer, Marker, useMapEvents } from 'react-leaflet';
import { useAuth } from '../../hooks/useAuth';
import { useTheme } from '../../hooks/useTheme';
import apiClient from '../../api/client';
import * as socialApi from '../../utils/socialApi';

const CAMEROON_CITIES = [
  'Douala', 'Yaoundé', 'Bafoussam', 'Bamenda', 'Garoua', 'Maroua', 'Ngaoundéré',
  'Bertoua', 'Ebolowa', 'Kribi', 'Limbe', 'Buea', 'Dschang', 'Edéa', 'Kumba',
  'Nkongsamba', 'Foumban', 'Bafang', 'Mbouda', 'Sangmélima',
];

const GENDERS = [
  { value: '', label: 'Preferer ne pas dire' },
  { value: 'homme', label: 'Homme' },
  { value: 'femme', label: 'Femme' },
  { value: 'autre', label: 'Autre' },
];

function MapClickHandler({ onPick }) {
  useMapEvents({
    click(e) { onPick(e.latlng.lat, e.latlng.lng); },
  });
  return null;
}

const Register = () => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { register } = useAuth();

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
  });

  // Champs du profil social (fusionnes dans l'inscription)
  const [username, setUsername] = useState('');
  const [usernameStatus, setUsernameStatus] = useState(null); // null | 'checking' | 'available' | 'taken'
  const [city, setCity] = useState('Douala');
  const [district, setDistrict] = useState('');
  const [districtSuggestions, setDistrictSuggestions] = useState([]);
  const [showDistrictSuggestions, setShowDistrictSuggestions] = useState(false);
  const [gender, setGender] = useState('');
  const [bio, setBio] = useState('');
  const [position, setPosition] = useState(null);

  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const usernameDebounce = useRef(null);
  const districtDebounce = useRef(null);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const defaultCenter = { Douala: [4.0511, 9.7679], Yaoundé: [3.8480, 11.5021] }[city] || [4.0511, 9.7679];

  useEffect(() => {
    if (usernameDebounce.current) clearTimeout(usernameDebounce.current);
    const clean = username.trim();
    if (clean.length < 3) {
      setUsernameStatus(null);
      return;
    }
    setUsernameStatus('checking');
    usernameDebounce.current = setTimeout(async () => {
      try {
        const res = await socialApi.checkUsername(clean);
        setUsernameStatus(res.available ? 'available' : 'taken');
      } catch (err) {
        setUsernameStatus(null);
      }
    }, 400);
  }, [username]);

  useEffect(() => {
    if (districtDebounce.current) clearTimeout(districtDebounce.current);
    if (district.trim().length < 2) {
      setDistrictSuggestions([]);
      return;
    }
    districtDebounce.current = setTimeout(async () => {
      try {
        const res = await apiClient.get(`/endpoints/quartiers-search.php?q=${encodeURIComponent(district)}&ville=${encodeURIComponent(city)}`);
        setDistrictSuggestions(res.results || []);
      } catch (err) {
        setDistrictSuggestions([]);
      }
    }, 300);
  }, [district, city]);

  const handleSelectDistrict = (quartier) => {
    setDistrict(quartier.nom);
    setPosition({ lat: quartier.latitude, lng: quartier.longitude });
    setShowDistrictSuggestions(false);
    setDistrictSuggestions([]);
  };

  const handleUseMyLocation = () => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => setPosition({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      () => setError("Impossible d'obtenir ta position, place le point manuellement sur la carte."),
      { enableHighAccuracy: true, timeout: 8000 }
    );
  };

  const handleMapPick = useCallback((lat, lng) => setPosition({ lat, lng }), []);

  const handleSubmit = async () => {
    setError('');

    if (!formData.name.trim() || !formData.email.trim() || !formData.password.trim()) {
      setError('Veuillez remplir tous les champs obligatoires');
      return;
    }
    if (formData.password.length < 6) {
      setError('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }
    if (formData.password !== formData.confirmPassword) {
      setError('Les mots de passe ne correspondent pas');
      return;
    }
    if (username.trim().length < 3) {
      setError("Le nom d'utilisateur doit faire au moins 3 caracteres");
      return;
    }
    if (usernameStatus === 'taken') {
      setError("Ce nom d'utilisateur est deja utilise. Choisis un autre nom.");
      return;
    }
    if (!city.trim() || !district.trim()) {
      setError('Ville et quartier sont requis');
      return;
    }

    setLoading(true);
    try {
      const newUser = await register(formData.name, formData.email, formData.phone, formData.password);

      try {
        await socialApi.createProfile({
          user_id: newUser.id,
          username: username.trim(),
          gender: gender || null,
          city: city.trim(),
          district: district.trim(),
          latitude: position ? position.lat : null,
          longitude: position ? position.lng : null,
          bio: bio.trim() || null,
        });
      } catch (profileErr) {
        // Le compte principal est cree meme si le profil social echoue (ex: username pris entre-temps).
        // Il pourra le completer plus tard via l'ecran Social (CreateProfileModal).
      }

      navigate('/');
    } catch (err) {
      setError(err.message || 'Erreur lors de la création du compte');
    } finally {
      setLoading(false);
    }
  };

  const styles = {
    container: {
      display: 'flex',
      flexDirection: 'column',
      minHeight: '100vh',
      padding: '16px',
      backgroundColor: theme.bg,
    },
    header: { display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' },
    backButton: { background: 'none', border: 'none', color: theme.textPrimary, fontSize: '20px', cursor: 'pointer' },
    logoSection: { textAlign: 'center', marginBottom: '24px' },
    logoTitle: { fontSize: '28px', fontWeight: '700', color: theme.gold },
    logoSubtitle: { fontSize: '13px', color: theme.textSecondary, marginTop: '4px' },
    form: { display: 'flex', flexDirection: 'column', gap: '14px' },
    field: { display: 'flex', flexDirection: 'column', gap: '6px', position: 'relative' },
    label: { fontSize: '13px', fontWeight: '600', color: theme.textSecondary },
    input: {
      padding: '14px 16px',
      backgroundColor: theme.bgInput,
      border: `1px solid ${theme.border}`,
      borderRadius: '8px',
      color: theme.textPrimary,
      fontSize: '15px',
      outline: 'none',
    },
    inputRow: { display: 'flex', alignItems: 'center', gap: '6px', padding: '4px 12px', backgroundColor: theme.bgInput, border: `1px solid ${theme.border}`, borderRadius: '8px' },
    atSign: { fontSize: '15px', fontWeight: '700', color: theme.gold },
    usernameInput: { flex: 1, border: 'none', outline: 'none', backgroundColor: 'transparent', color: theme.textPrimary, fontSize: '15px', padding: '10px 0' },
    select: { padding: '14px 16px', backgroundColor: theme.bgInput, border: `1px solid ${theme.border}`, borderRadius: '8px', color: theme.textPrimary, fontSize: '15px', outline: 'none' },
    textarea: { padding: '14px 16px', backgroundColor: theme.bgInput, border: `1px solid ${theme.border}`, borderRadius: '8px', color: theme.textPrimary, fontSize: '14px', outline: 'none', minHeight: '70px', resize: 'vertical', fontFamily: 'inherit' },
    hint: { fontSize: '11px', color: theme.textSecondary },
    statusOk: { fontSize: '11px', color: '#48BB78', fontWeight: '600' },
    statusTaken: { fontSize: '11px', color: '#F56565', fontWeight: '600' },
    suggestions: { position: 'absolute', top: '100%', left: 0, right: 0, backgroundColor: theme.bgInput, border: `1px solid ${theme.border}`, borderRadius: '10px', marginTop: '4px', zIndex: 50, overflow: 'hidden' },
    suggestionItem: { padding: '10px 12px', fontSize: '13px', color: theme.textPrimary, cursor: 'pointer', borderBottom: `1px solid ${theme.border}` },
    mapWrap: { height: '200px', borderRadius: '10px', overflow: 'hidden', border: `1px solid ${theme.border}` },
    geoBtn: { alignSelf: 'flex-start', padding: '8px 12px', fontSize: '12px', fontWeight: '700', color: theme.gold, background: 'none', border: `1px solid ${theme.gold}`, borderRadius: '8px', cursor: 'pointer' },
    sectionLabel: { fontSize: '14px', fontWeight: '700', color: theme.gold, marginTop: '4px' },
    errorText: { fontSize: '13px', color: '#F56565', textAlign: 'center' },
    button: {
      padding: '16px',
      backgroundColor: theme.gold,
      border: 'none',
      borderRadius: '10px',
      color: theme.mode === 'light' ? '#FFFFFF' : '#0A0E1A',
      fontSize: '16px',
      fontWeight: '700',
      cursor: 'pointer',
      marginTop: '8px',
    },
    links: { display: 'flex', justifyContent: 'center', gap: '6px', marginTop: '20px' },
    link: { color: theme.gold, fontSize: '14px', cursor: 'pointer' },
    text: { color: theme.textSecondary, fontSize: '14px' },
  };

  return (
    <div style={styles.container}>
      <Helmet>
        <title>Inscription | Fynho</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div style={styles.header}>
        <button style={styles.backButton} onClick={() => navigate('/')}>←</button>
      </div>

      <div style={styles.logoSection}>
        <div style={styles.logoTitle}>Fynho</div>
        <div style={styles.logoSubtitle}>Créez votre compte</div>
      </div>

      <div style={styles.form}>
        <div style={styles.field}>
          <label style={styles.label}>Nom complet</label>
          <input type="text" style={styles.input} value={formData.name} onChange={(e) => handleChange('name', e.target.value)} placeholder="Jean Kamga" />
        </div>
        <div style={styles.field}>
          <label style={styles.label}>Email</label>
          <input type="email" style={styles.input} value={formData.email} onChange={(e) => handleChange('email', e.target.value)} placeholder="votre@email.com" />
        </div>
        <div style={styles.field}>
          <label style={styles.label}>Téléphone</label>
          <input type="tel" style={styles.input} value={formData.phone} onChange={(e) => handleChange('phone', e.target.value)} placeholder="+237 6XX XXX XXX" />
        </div>
        <div style={styles.field}>
          <label style={styles.label}>Mot de passe</label>
          <input type="password" style={styles.input} value={formData.password} onChange={(e) => handleChange('password', e.target.value)} placeholder="••••••••" />
        </div>
        <div style={styles.field}>
          <label style={styles.label}>Confirmer le mot de passe</label>
          <input type="password" style={styles.input} value={formData.confirmPassword} onChange={(e) => handleChange('confirmPassword', e.target.value)} placeholder="••••••••" />
        </div>

        <div style={styles.sectionLabel}>Ton profil Fynho Social</div>

        <div style={styles.field}>
          <label style={styles.label}>Nom d'utilisateur</label>
          <div style={styles.inputRow}>
            <span style={styles.atSign}>@</span>
            <input
              style={styles.usernameInput}
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value.replace(/\s/g, '').toLowerCase())}
              placeholder="yvan237"
            />
          </div>
          {usernameStatus === 'checking' && <span style={styles.hint}>Verification...</span>}
          {usernameStatus === 'available' && <span style={styles.statusOk}>✓ Disponible</span>}
          {usernameStatus === 'taken' && <span style={styles.statusTaken}>Ce nom d'utilisateur est deja utilise. Choisis un autre nom.</span>}
        </div>

        <div style={styles.field}>
          <label style={styles.label}>Ville</label>
          <input
            style={styles.input}
            list="register-city-list"
            value={city}
            onChange={(e) => setCity(e.target.value)}
            placeholder="Ex: Douala"
          />
          <datalist id="register-city-list">
            {CAMEROON_CITIES.map((c) => <option key={c} value={c} />)}
          </datalist>
        </div>

        <div style={styles.field}>
          <label style={styles.label}>Quartier</label>
          <input
            style={styles.input}
            type="text"
            value={district}
            onChange={(e) => { setDistrict(e.target.value); setShowDistrictSuggestions(true); }}
            onFocus={() => setShowDistrictSuggestions(true)}
            placeholder="Ex: Bonapriso, ou un nouveau nom"
          />
          <span style={styles.hint}>Pas trouve ? Tape simplement le nom, il sera accepte quand meme.</span>
          {showDistrictSuggestions && districtSuggestions.length > 0 && (
            <div style={styles.suggestions}>
              {districtSuggestions.map((q) => (
                <div key={q.id} style={styles.suggestionItem} onClick={() => handleSelectDistrict(q)}>
                  📍 {q.nom} — {q.ville}
                </div>
              ))}
            </div>
          )}
        </div>

        <div style={styles.field}>
          <label style={styles.label}>Position sur la carte (facultatif, ajustable)</label>
          <button type="button" style={styles.geoBtn} onClick={handleUseMyLocation}>📍 Utiliser ma position actuelle</button>
          <div style={styles.mapWrap}>
            <MapContainer center={position ? [position.lat, position.lng] : defaultCenter} zoom={13} style={{ width: '100%', height: '100%' }}>
              <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
              <MapClickHandler onPick={handleMapPick} />
              {position && <Marker position={[position.lat, position.lng]} />}
            </MapContainer>
          </div>
          <span style={styles.hint}>Clique sur la carte pour ajuster — jamais ta position exacte, juste ton quartier.</span>
        </div>

        <div style={styles.field}>
          <label style={styles.label}>Genre (facultatif)</label>
          <select style={styles.select} value={gender} onChange={(e) => setGender(e.target.value)}>
            {GENDERS.map((g) => <option key={g.value} value={g.value}>{g.label}</option>)}
          </select>
        </div>

        <div style={styles.field}>
          <label style={styles.label}>Bio (facultatif)</label>
          <textarea style={styles.textarea} value={bio} onChange={(e) => setBio(e.target.value)} placeholder="Quelques mots sur toi..." maxLength={280} />
        </div>

        {error && <div style={styles.errorText}>{error}</div>}
        <button style={{ ...styles.button, opacity: loading ? 0.6 : 1 }} onClick={handleSubmit} disabled={loading}>
          {loading ? 'Création...' : "S'inscrire"}
        </button>
      </div>

      <div style={styles.links}>
        <span style={styles.text}>Déjà un compte ?</span>
        <span style={styles.link} onClick={() => navigate('/login')}>Se connecter</span>
      </div>
    </div>
  );
};

export default Register;